import React, { forwardRef } from 'react';
import { Input, InputProps } from '@chakra-ui/react';

export interface DateInputProps extends Omit<InputProps, 'type' | 'value' | 'onChange'> {
  value?: string | null;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  name?: string;
}

const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  function DateInput({ value, onChange, onBlur, name, ...props }, ref) {
    
    // Format date for input (YYYY-MM-DD)
    const formatDateForInput = (dateString?: string | null) => {
      if (!dateString) return '';
      const directDateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
      if (directDateMatch) {
        return directDateMatch[1];
      }
      try {
        const normalizedValue = dateString.includes(' ') && !dateString.includes('T')
          ? dateString.replace(' ', 'T')
          : dateString;
        const date = new Date(normalizedValue);
        // Check if date is valid
        if (isNaN(date.getTime())) return '';
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch {
        return '';
      }
    };

    // Handle date change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const dateValue = e.target.value;
      if (onChange) {
        // Convert to ISO string if date is selected, otherwise pass empty string
        if (dateValue) {
          // Create end of day in user's local timezone, then convert to UTC
          const date = new Date(dateValue + 'T23:59:59.999');
          onChange(date.toISOString());
        } else {
          onChange('');
        }
      }
    };

    return (
      <Input
        ref={ref}
        type="date"
        value={formatDateForInput(value)}
        onChange={handleChange}
        onBlur={onBlur}
        name={name}
        bg="white"
        _dark={{ bg: "gray.700" }}
        borderColor="gray.300"
        _hover={{ borderColor: "gray.400" }}
        _focus={{ 
          borderColor: "brand.600", 
          boxShadow: "0 0 0 1px var(--chakra-colors-brand-600)" 
        }}
        {...props}
      />
    );
  }
);

export default DateInput; 
